/**
 * ChatInput
 *
 * Text input with Enter to send, Shift+Enter for new line.
 * Attachment icon (📎) for file upload with preview.
 * Triggers slash command dropdown when user types '/'.
 * '#' icon opens the ContextPickerMenu for AI Actions and context attachments.
 * Supports @mention autocomplete for orchestration entities when on Orchestration page.
 *
 * Requirements: 6.1, 9.1, 9.2, 9.4, 9.5, 11.4, 12.1, 12.2, 12.3, 12.4, 12.5
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Send, Paperclip, X, FileText, Hash } from 'lucide-react';
import SlashCommandDropdown from './SlashCommandDropdown';
import { ContextPickerMenu, CONTEXT_TOKENS } from '../../ai-generation/components/ContextPickerMenu';
import { createMentionToken, extractMentionsFromRawText, ENTITY_TYPES } from '../../orchestration/models/entityMention';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'application/json', 'text/csv'];

// ─── @Mention Autocomplete Helpers ───────────────────────────────────────────

/**
 * Case-insensitive fuzzy filter: checks if all characters in the query
 * appear in order within the target string.
 */
function fuzzyMatch(query, target) {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

/**
 * Build a flat list of mentionable entities from projectRequests, collections, and folders.
 */
function buildEntityList(projectRequests, collections, folders) {
  const entities = [];

  if (Array.isArray(projectRequests)) {
    for (const req of projectRequests) {
      if (!req) continue;
      entities.push({
        id: req.request_id ?? req.id,
        type: ENTITY_TYPES.API,
        name: req.request_name || req.name || 'Unnamed API',
        meta: {
          method: req.method_name || req.method || 'GET',
          url: req.raw_url || req.url || '',
        },
      });
    }
  }

  if (Array.isArray(collections)) {
    for (const col of collections) {
      if (!col) continue;
      entities.push({
        id: col.id,
        type: ENTITY_TYPES.COLLECTION,
        name: col.name || col.collection_name || 'Unnamed Collection',
        meta: { apiCount: col.requests?.length ?? col.apiCount ?? 0 },
      });
    }
  }

  if (Array.isArray(folders)) {
    for (const folder of folders) {
      if (!folder) continue;
      entities.push({
        id: folder.id,
        type: ENTITY_TYPES.FOLDER,
        name: folder.name || folder.folder_name || 'Unnamed Folder',
        meta: { apiCount: folder.requests?.length ?? folder.apiCount ?? 0 },
      });
    }
  }

  return entities;
}

/** Category labels for rendering */
const MENTION_CATEGORY_LABELS = {
  [ENTITY_TYPES.API]: 'APIs',
  [ENTITY_TYPES.COLLECTION]: 'Collections',
  [ENTITY_TYPES.FOLDER]: 'Folders',
};

/** Category display order */
const MENTION_CATEGORY_ORDER = [ENTITY_TYPES.API, ENTITY_TYPES.COLLECTION, ENTITY_TYPES.FOLDER];

/** Method color map for API badges */
const MENTION_METHOD_COLORS = {
  GET: 'bg-emerald-500/20 text-emerald-400',
  POST: 'bg-amber-500/20 text-amber-400',
  PUT: 'bg-blue-500/20 text-blue-400',
  PATCH: 'bg-violet-500/20 text-violet-400',
  DELETE: 'bg-red-500/20 text-red-400',
};

/**
 * @param {Object} props
 * @param {Function} props.onSend - called with (text, attachedFile?, contextAttachments?, mentions?)
 * @param {boolean} props.isStreaming
 * @param {Function} props.onUploadFile - called with File, returns promise
 * @param {Array} [props.commands] - slash commands for dropdown
 * @param {Function} [props.onSelectAIAction] - called when user selects an AI Action from # menu
 * @param {Function} [props.onSelectContext] - called when user selects a context attachment from # menu
 * @param {string} [props.currentPage] - current page identifier (e.g., "Orchestration")
 * @param {Array} [props.projectRequests] - project APIs for @mention (only used on Orchestration page)
 * @param {Array} [props.collections] - collections for @mention (only used on Orchestration page)
 * @param {Array} [props.folders] - folders for @mention (only used on Orchestration page)
 */
export function ChatInput({ onSend, isStreaming, onUploadFile, commands = [], onSelectAIAction, onSelectContext, currentPage, projectRequests = [], collections = [], folders = [] }) {
  const [text, setText] = useState('');
  const [attachedFile, setAttachedFile] = useState(null); // { file, preview }
  const [fileError, setFileError] = useState(null);
  const [showSlashDropdown, setShowSlashDropdown] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');
  const [showContextPicker, setShowContextPicker] = useState(false);
  const [contextAttachments, setContextAttachments] = useState([]); // Array of context token keys
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const contextPickerAnchorRef = useRef(null);

  // ─── @Mention state (active on Orchestration page) ────────────────────────
  const [mentionTokens, setMentionTokens] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const mentionDropdownRef = useRef(null);

  // Determine if @mention is enabled (only on Orchestration page with available entities)
  const mentionEnabled = currentPage === 'Orchestration';

  // Build entity list for @mention autocomplete
  const allMentionEntities = useMemo(
    () => mentionEnabled ? buildEntityList(projectRequests, collections, folders) : [],
    [mentionEnabled, projectRequests, collections, folders]
  );

  // Filter entities based on current mention query
  const filteredMentionEntities = useMemo(() => {
    if (!showMentionDropdown || !mentionEnabled) return [];
    if (!mentionQuery) return allMentionEntities;
    return allMentionEntities.filter((entity) => fuzzyMatch(mentionQuery, entity.name));
  }, [allMentionEntities, mentionQuery, showMentionDropdown, mentionEnabled]);

  // Group filtered entities by category
  const groupedMentionEntities = useMemo(() => {
    const groups = {};
    for (const cat of MENTION_CATEGORY_ORDER) {
      const items = filteredMentionEntities.filter((e) => e.type === cat);
      if (items.length > 0) groups[cat] = items;
    }
    return groups;
  }, [filteredMentionEntities]);

  // Flat list for keyboard navigation
  const flatMentionFiltered = useMemo(() => {
    const flat = [];
    for (const cat of MENTION_CATEGORY_ORDER) {
      if (groupedMentionEntities[cat]) {
        flat.push(...groupedMentionEntities[cat]);
      }
    }
    return flat;
  }, [groupedMentionEntities]);

  // Clamp activeIndex when filtered list changes
  useEffect(() => {
    if (mentionActiveIndex >= flatMentionFiltered.length) {
      setMentionActiveIndex(Math.max(0, flatMentionFiltered.length - 1));
    }
  }, [flatMentionFiltered.length, mentionActiveIndex]);

  // Scroll active mention item into view
  useEffect(() => {
    if (!showMentionDropdown || !mentionDropdownRef.current) return;
    const activeEl = mentionDropdownRef.current.querySelector('[data-mention-active="true"]');
    if (activeEl && typeof activeEl.scrollIntoView === 'function') {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [mentionActiveIndex, showMentionDropdown]);

  // ─── @Mention insertion ───────────────────────────────────────────────────
  const insertMention = useCallback((entity) => {
    if (mentionStartPos === null) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea?.selectionStart ?? text.length;

    const before = text.slice(0, mentionStartPos);
    const after = text.slice(cursorPos);

    const pillText = `@${entity.name}`;
    const newText = before + pillText + ' ' + after;

    const token = createMentionToken(
      entity.name,
      { entityId: entity.id, entityType: entity.type, entityName: entity.name },
      mentionStartPos,
      mentionStartPos + pillText.length
    );

    // Shift existing tokens that come after the insertion point
    const insertedLength = pillText.length + 1;
    const removedLength = cursorPos - mentionStartPos;
    const shift = insertedLength - removedLength;

    const updatedTokens = mentionTokens.map((t) => {
      if (t.startIndex >= mentionStartPos) {
        return { ...t, startIndex: t.startIndex + shift, endIndex: t.endIndex + shift };
      }
      return t;
    });

    setMentionTokens([...updatedTokens, token]);
    setText(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');
    setMentionStartPos(null);
    setMentionActiveIndex(0);

    // Restore cursor position
    setTimeout(() => {
      if (textarea) {
        const newCursorPos = mentionStartPos + pillText.length + 1;
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [mentionStartPos, text, mentionTokens]);

  const handleTextChange = (e) => {
    const val = e.target.value;
    const cursorPos = e.target.selectionStart;
    setText(val);

    // Auto-resize textarea
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 150) + 'px';
    }

    // Slash command detection
    if (val.startsWith('/')) {
      setShowSlashDropdown(true);
      setSlashFilter(val.slice(1));
    } else {
      setShowSlashDropdown(false);
      setSlashFilter('');
    }

    // '#' context picker detection — show when user types '#' at start or after a space
    if (val.endsWith('#') && (val.length === 1 || val[val.length - 2] === ' ')) {
      setShowContextPicker(true);
    }

    // ─── @Mention detection ─────────────────────────────────────────────────
    if (mentionEnabled) {
      if (showMentionDropdown && mentionStartPos !== null) {
        // We're in an active mention flow — update query or dismiss
        if (cursorPos <= mentionStartPos || val[mentionStartPos] !== '@') {
          setShowMentionDropdown(false);
          setMentionQuery('');
          setMentionStartPos(null);
        } else {
          const query = val.slice(mentionStartPos + 1, cursorPos);
          if (query.length === 0 && val[cursorPos - 1] === ' ') {
            setShowMentionDropdown(false);
            setMentionQuery('');
            setMentionStartPos(null);
          } else {
            setMentionQuery(query);
            setMentionActiveIndex(0);
          }
        }
      } else {
        // Detect new @ trigger
        if (
          cursorPos > 0 &&
          val[cursorPos - 1] === '@' &&
          (cursorPos === 1 || /\s/.test(val[cursorPos - 2]))
        ) {
          setMentionStartPos(cursorPos - 1);
          setMentionQuery('');
          setShowMentionDropdown(true);
          setMentionActiveIndex(0);
        }
      }
    }
  };

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    // Extract mentions if any are present
    let mentions = undefined;
    if (mentionEnabled && mentionTokens.length > 0) {
      const extracted = extractMentionsFromRawText(trimmed, mentionTokens);
      mentions = extracted.mentions;
    }

    onSend(trimmed, attachedFile?.file, contextAttachments.length > 0 ? contextAttachments : undefined, mentions);
    setText('');
    setAttachedFile(null);
    setContextAttachments([]);
    setMentionTokens([]);
    setShowSlashDropdown(false);
    setSlashFilter('');
    setShowContextPicker(false);
    setShowMentionDropdown(false);
    setMentionQuery('');
    setMentionStartPos(null);
    // Reset textarea height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [text, isStreaming, onSend, attachedFile, contextAttachments, mentionEnabled, mentionTokens]);

  const handleContextSelect = (contextKey) => {
    const token = CONTEXT_TOKENS[contextKey];
    if (!token) return;
    // Insert the token into the text, replacing the trailing '#' if present
    setText((prev) => {
      const trimmedHash = prev.endsWith('#') ? prev.slice(0, -1) : prev;
      const prefix = trimmedHash && !trimmedHash.endsWith(' ') ? trimmedHash + ' ' : trimmedHash;
      return prefix + token.token + ' ';
    });
    // Track attachment
    if (!contextAttachments.includes(contextKey)) {
      setContextAttachments((prev) => [...prev, contextKey]);
    }
    setShowContextPicker(false);
    onSelectContext?.(contextKey);
    // Focus back on textarea
    textareaRef.current?.focus();
  };

  const handleAIActionSelect = (action) => {
    // Insert mode indicator and pre-fill prompt
    const prefix = `[${action.label}] `;
    setText(prefix);
    setShowContextPicker(false);
    onSelectAIAction?.(action);
    // Focus back on textarea
    textareaRef.current?.focus();
  };

  const removeContextAttachment = (key) => {
    setContextAttachments((prev) => prev.filter((k) => k !== key));
    // Also remove the token from text
    const token = CONTEXT_TOKENS[key]?.token;
    if (token) {
      setText((prev) => prev.replace(token, '').replace(/\s{2,}/g, ' ').trim());
    }
  };

  const handleKeyDown = (e) => {
    // @Mention dropdown keyboard navigation
    if (showMentionDropdown && flatMentionFiltered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionActiveIndex((prev) => (prev + 1) % flatMentionFiltered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionActiveIndex((prev) => (prev - 1 + flatMentionFiltered.length) % flatMentionFiltered.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(flatMentionFiltered[mentionActiveIndex]);
        return;
      }
    }
    if (showMentionDropdown && e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
      setMentionQuery('');
      setMentionStartPos(null);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError(null);

    // Client-side validation
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File exceeds 10MB limit');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Unsupported file type. Allowed: PNG, JPG, GIF, JSON, CSV');
      return;
    }

    const isImage = file.type.startsWith('image/');
    const preview = isImage ? URL.createObjectURL(file) : null;
    setAttachedFile({ file, preview });

    // Upload immediately
    onUploadFile?.(file)?.catch((err) => {
      setFileError(err.message || 'Upload failed');
      setAttachedFile(null);
    });
  };

  const removeAttachment = () => {
    if (attachedFile?.preview) URL.revokeObjectURL(attachedFile.preview);
    setAttachedFile(null);
    setFileError(null);
  };

  const handleSlashSelect = (command) => {
    setText(command.slash_command || command.slashCommand || command);
    setShowSlashDropdown(false);
    // Auto-send the slash command
    setTimeout(() => {
      onSend(command.slash_command || command.slashCommand || command);
      setText('');
    }, 0);
  };

  return (
    <div className="border-t border-gray-700/40 px-3 py-2 shrink-0">
      {/* File preview */}
      {attachedFile && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-card rounded-lg text-xs">
          {attachedFile.preview ? (
            <img src={attachedFile.preview} alt="preview" className="w-8 h-8 rounded object-cover" />
          ) : (
            <FileText className="w-5 h-5 text-foreground-secondary" />
          )}
          <span className="truncate flex-1 text-foreground-secondary">{attachedFile.file.name}</span>
          <button onClick={removeAttachment} className="p-0.5 hover:text-foreground">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* File error */}
      {fileError && (
        <div className="text-xs text-red-500 mb-2 px-2">{fileError}</div>
      )}

      {/* Slash command dropdown */}
      {showSlashDropdown && (
        <SlashCommandDropdown
          commands={commands}
          filter={slashFilter}
          onSelect={handleSlashSelect}
          onClose={() => setShowSlashDropdown(false)}
        />
      )}

      {/* Context attachments badges */}
      {contextAttachments.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2 px-2 flex-wrap">
          {contextAttachments.map((key) => {
            const token = CONTEXT_TOKENS[key];
            if (!token) return null;
            const Icon = token.icon;
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 rounded text-label text-blue-400"
              >
                <Icon className="w-3 h-3" />
                {token.label}
                <button
                  onClick={() => removeContextAttachment(key)}
                  className="ml-0.5 hover:text-blue-200"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* @Mention pills (shown when mentions are active on orchestration page) */}
      {mentionEnabled && mentionTokens.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2 px-2 flex-wrap">
          {mentionTokens.map((token, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 border border-primary/20 rounded text-label text-primary"
            >
              <span className="opacity-60">@</span>
              {token.displayText}
              <button
                onClick={() => {
                  // Remove this mention token and adjust text
                  const before = text.slice(0, token.startIndex);
                  const after = text.slice(token.endIndex);
                  const newText = before + after;
                  const removedLength = token.endIndex - token.startIndex;

                  const updatedTokens = mentionTokens
                    .filter((_, i) => i !== idx)
                    .map((t) => {
                      if (t.startIndex > token.startIndex) {
                        return { ...t, startIndex: t.startIndex - removedLength, endIndex: t.endIndex - removedLength };
                      }
                      return t;
                    });

                  setText(newText);
                  setMentionTokens(updatedTokens);
                }}
                className="ml-0.5 hover:text-primary/80"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2">
        {/* Context picker # button */}
        <div className="relative shrink-0" ref={contextPickerAnchorRef}>
          <button
            onClick={() => setShowContextPicker(!showContextPicker)}
            title="Context picker (#)"
            className={`p-2 rounded-lg hover:bg-card transition-colors shrink-0 ${
              showContextPicker ? 'text-blue-400 bg-card' : 'text-foreground-secondary hover:text-foreground'
            }`}
          >
            <Hash className="w-4 h-4" />
          </button>
          <ContextPickerMenu
            isOpen={showContextPicker}
            onClose={() => setShowContextPicker(false)}
            onSelectContext={handleContextSelect}
            onSelectAIAction={handleAIActionSelect}
          />
        </div>

        {/* Attachment button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
          className="p-2 rounded-lg hover:bg-card text-foreground-secondary hover:text-foreground transition-colors shrink-0"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.gif,.json,.csv"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Textarea with @mention dropdown */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={mentionEnabled ? 'Type a message... (@ to mention APIs)' : 'Type a message...'}
            rows={1}
            disabled={isStreaming}
            className="w-full resize-none bg-[#2a2d35] rounded-lg px-3 py-2 text-xs text-gray-200
                       placeholder:text-gray-500 outline-none border border-gray-700/40
                       focus:border-gray-500 transition-colors overflow-y-auto
                       disabled:opacity-50"
            style={{ minHeight: '36px', maxHeight: '150px' }}
          />

          {/* @Mention autocomplete dropdown */}
          {showMentionDropdown && mentionEnabled && (
            <div
              ref={mentionDropdownRef}
              className="absolute z-50 bottom-full mb-1 left-0 w-full max-h-[200px] overflow-y-auto rounded-lg border border-gray-700/50 bg-[#1e2028] shadow-lg"
              role="listbox"
              aria-label="Mention suggestions"
            >
              {flatMentionFiltered.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-gray-500 text-center">
                  No matches found
                </div>
              ) : (
                MENTION_CATEGORY_ORDER.map((cat) => {
                  const items = groupedMentionEntities[cat];
                  if (!items) return null;
                  return (
                    <div key={cat}>
                      <div className="px-3 py-1 text-label font-semibold text-gray-500 uppercase tracking-wider bg-gray-800/50 sticky top-0">
                        {MENTION_CATEGORY_LABELS[cat]}
                      </div>
                      {items.map((entity) => {
                        const globalIdx = flatMentionFiltered.indexOf(entity);
                        const isActive = globalIdx === mentionActiveIndex;
                        return (
                          <div
                            key={`${entity.type}-${entity.id}`}
                            data-mention-active={isActive}
                            role="option"
                            aria-selected={isActive}
                            className={`
                              flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs transition-colors
                              ${isActive ? 'bg-blue-500/10 text-gray-200' : 'text-gray-400 hover:bg-white/5'}
                            `.trim()}
                            onClick={() => insertMention(entity)}
                            onMouseEnter={() => setMentionActiveIndex(globalIdx)}
                          >
                            {entity.type === ENTITY_TYPES.API ? (
                              <>
                                <span className={`px-1 py-0.5 rounded text-label font-bold uppercase ${MENTION_METHOD_COLORS[entity.meta.method?.toUpperCase()] || 'bg-gray-700 text-gray-400'}`}>
                                  {entity.meta.method || 'GET'}
                                </span>
                                <span className="flex-1 truncate font-medium">{entity.name}</span>
                                <span className="text-label text-gray-600 truncate max-w-[120px]">
                                  {entity.meta.url || ''}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="px-1 py-0.5 rounded text-label font-bold uppercase bg-gray-700 text-gray-400">
                                  {entity.type === ENTITY_TYPES.COLLECTION ? 'COL' : 'FLD'}
                                </span>
                                <span className="flex-1 truncate font-medium">{entity.name}</span>
                                {entity.meta.apiCount != null && (
                                  <span className="text-label text-gray-600">
                                    {entity.meta.apiCount} API{entity.meta.apiCount !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!text.trim() || isStreaming}
          title="Send (Enter)"
          className="p-2 rounded-lg bg-primary text-primary-foreground
                     disabled:opacity-40 disabled:cursor-not-allowed
                     hover:bg-primary/90 transition-colors shrink-0"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default ChatInput;
